/**
 * Model reprezentujący plik multimedialny (Zdjęcie, Wideo, Dokument) w systemie plików urządzenia.
 * Klasa ta pełni rolę "pomostu" między lokalnym systemem plików (na urządzeniu mobilnym) a chmurą (Object Storage).
 * Obsługuje logikę synchronizacji (flaga `isSynced`), typowania MIME oraz ścieżek natywnych (`file://`).
 * Powiązana z pacjentem (`petUUID`) lub konkretną wizytą (`vetReservationUUID`).
 */
export class VetineoFile {
  /** Unikalny identyfikator pliku w bazie danych (UUID). */
  public uuid: string;

  /** Nazwa pliku wraz z rozszerzeniem. */
  public name: string;

  /**
   * Lokalna ścieżka do katalogu, w którym znajduje się plik (często zawiera prefiks `file://`).
   * Używana przez system plików urządzenia mobilnego.
   */
  public path: string;

  /** Typ pliku (np. kategoria wewnętrzna lub rozszerzenie). */
  public type: string;

  /** Data ostatniej modyfikacji pliku (metadata z systemu plików). */
  public lastModifiedDate: Date;

  /** Rozmiar pliku w bajtach. */
  public size: number;

  /**
   * UUID wizyty, do której przypisany jest ten plik.
   */
  public visitUUID: string;
  /**
   * Flaga synchronizacji.
   * - `true`: Plik został pomyślnie wysłany na serwer.
   * - `false`: Plik istnieje tylko lokalnie i oczekuje na upload (kolejka synchronizacji).
   */
  public isSynced = false;

  /** ID użytkownika aplikacji, który utworzył/dodał ten plik. */
  public appUserId: number;

  /** Data przesłania pliku na serwer. */
  public uploaded: Date;

  /**
   * Typ MIME pliku (np. 'image/jpeg', 'video/mp4').
   * Ustawiany automatycznie przez metodę `setContentType()`.
   */
  public contentType: string;

  /**
   * Klucz (ścieżka) do zasobu w chmurze.
   * Służy do pobierania pliku na innych urządzeniach.
   */
  public objectStorageKey: string;

  constructor() {
  }

  /**
   * Zwraca pełną ścieżkę natywną do pliku, gotową do użycia przez wtyczki systemowe.
   * Usuwa prefiks `file://`, który może powodować problemy w niektórych API natywnych Android/iOS.
   *
   * @returns Pełna ścieżka (katalog + nazwa pliku) bez schematu URI.
   */
  getNativePath(): string {
    return `${this.path}/${this.name}`.replace('file://', '');
  }

  /**
   * Automatycznie ustawia pole `contentType` na podstawie rozszerzenia w nazwie pliku (`name`).
   * Obsługuje podstawowe formaty multimedialne: JPG, MP4, MOV.
   * Wywoływana przed wysłaniem pliku na serwer, aby ustawić odpowiedni nagłówek HTTP.
   */
  setContentType(): void {
    if (this.name) {
      if (this.name.indexOf('.jpg') > 0) { this.contentType = 'image/jpeg'; }
      if (this.name.indexOf('.mp4') > 0) { this.contentType = 'video/mp4'; }
      if (this.name.indexOf('.mov') > 0) { this.contentType = 'video/quicktime'; }
    }
  }
}
